import React, { useState } from 'react';
import {
  Paper,
  Stack,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  Description as DescriptionIcon,
  Code as CodeIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { GhsPictogramSelector } from '../products/GhsPictogramSelector';
import { ProductHeaderProps } from './types';
import { getLanguageDisplayName } from './utils';

export const ProductHeader: React.FC<ProductHeaderProps> = ({
  product,
  translations,
  isEditing,
  editForm,
  fieldErrors,
  canAddSds,
  canReplaceSds,
  canDeleteSds,
  canManageEmbed,
  onEdit,
  onSave,
  onCancel,
  onFormChange,
  onPictogramsChange,
  onNavigateToTranslate,
  onDelete,
  onManageEmbed,
  onManageSds,
  onSdsDownload
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const effectiveFieldErrors = fieldErrors || {};
  const attachedSds = product.sds;
  const showSdsBadge = attachedSds || canAddSds;

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDeleteClick = () => {
    onDelete();
    handleMenuClose();
  };

  return (
    <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1.25,
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5} justifyContent="space-between" alignItems="flex-start">
          <Box flex={1} mr={3}>
            {isEditing ? (
              <Stack spacing={1.5} mb={1}>
                <TextField
                  fullWidth
                  size="small"
                  label={t('products.productName')}
                  value={editForm.name}
                  onChange={(e) => onFormChange('name', e.target.value)}
                  placeholder={t('products.productNamePlaceholder')}
                  error={Boolean(effectiveFieldErrors.name)}
                  helperText={effectiveFieldErrors.name || ' '}
                />
                <Stack direction="row" spacing={1.5}>
                  <TextField
                    size="small"
                    label={t('products.sku')}
                    value={editForm.sku}
                    onChange={(e) => onFormChange('sku', e.target.value)}
                    placeholder={t('products.skuPlaceholder')}
                    error={Boolean(effectiveFieldErrors.sku)}
                    helperText={effectiveFieldErrors.sku}
                  />
                  <TextField
                    size="small"
                    label={t('products.ean')}
                    value={editForm.ean}
                    onChange={(e) => onFormChange('ean', e.target.value)}
                    placeholder={t('products.eanPlaceholder')}
                    error={Boolean(effectiveFieldErrors.ean)}
                    helperText={effectiveFieldErrors.ean}
                  />
                </Stack>
                <GhsPictogramSelector
                  value={editForm.ghs_pictograms}
                  onChange={onPictogramsChange}
                />
              </Stack>
            ) : (
              <>
                <Typography variant="h5" fontWeight={700} color="text.primary" mb={1}>
                  {product.name}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" mb={1.5}>
                  {product.sku && (
                    <Chip label={`${t('products.sku')}: ${product.sku}`} size="small" variant="outlined" />
                  )}
                  {product.ean && (
                    <Chip label={`${t('products.ean')}: ${product.ean}`} size="small" variant="outlined" />
                  )}
                  {product.base_language && (
                    <Chip label={getLanguageDisplayName(product.base_language)} size="small" color="primary" variant="outlined" />
                  )}
                </Stack>

                {showSdsBadge && (
                  <Box
                    onClick={onManageSds}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.75,
                      mb: (product.ghs_pictograms?.length ?? 0) > 0 ? 1.5 : 0,
                      px: 1.25,
                      py: 0.5,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: attachedSds ? 'primary.light' : 'divider',
                      bgcolor: attachedSds ? 'primary.50' : 'transparent',
                      color: attachedSds ? 'primary.main' : 'text.secondary',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                      '&:hover': {
                        borderColor: attachedSds ? 'primary.main' : 'text.secondary',
                        bgcolor: attachedSds ? 'primary.100' : 'action.hover',
                      },
                    }}
                  >
                    <DescriptionIcon sx={{ fontSize: 15 }} />
                    <Typography variant="caption" fontWeight={600} lineHeight={1}>
                      {t('productDetails.sdsTitle')}
                    </Typography>
                  </Box>
                )}

                {(product.ghs_pictograms?.length ?? 0) > 0 && (
                  <GhsPictogramSelector value={product.ghs_pictograms ?? []} readOnly />
                )}
              </>
            )}
          </Box>

          {isEditing ? (
            <Stack direction="row" spacing={1}>
              <Button size="small" variant="contained" startIcon={<SaveIcon />} onClick={onSave}>
                {t('productDetails.save')}
              </Button>
              <Button size="small" variant="outlined" startIcon={<CancelIcon />} onClick={onCancel}>
                {t('productDetails.cancel')}
              </Button>
            </Stack>
          ) : (
            <Stack direction="row" spacing={1} alignItems="center">
              {canManageEmbed && (
                <Button variant="outlined" startIcon={<CodeIcon />} onClick={onManageEmbed}>
                  {t('productDetails.embed')}
                </Button>
              )}
              <Button variant="outlined" onClick={onEdit}>
                {t('productDetails.edit')}
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={onNavigateToTranslate}
                sx={{ fontWeight: 600 }}
              >
                {t('products.add')}
              </Button>
              <IconButton onClick={handleMenuClick} sx={{ color: 'text.secondary' }}>
                <MoreVertIcon />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
                  <ListItemIcon>
                    <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
                  </ListItemIcon>
                  <ListItemText>{t('productDetails.deleteProduct')}</ListItemText>
                </MenuItem>
              </Menu>
            </Stack>
          )}
        </Stack>
    </Paper>
  );
};
